import React, { useEffect, useMemo, useRef, useState, ReactNode } from 'react';
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom';
import { BackLayerProvider, type BackLayerRegistry } from '@heltarchat/ui';

/*
 * The back stack. Every open layer (chat, contact details, dialog, menu,
 * viewer) owns one history entry, so the system back button has something to
 * step back to. Each entry is tagged in history.state.usr with how many layers
 * are open on it, and one rule holds: the stack is as deep as the entry
 * history is on. A layer closes -> step back. One opens -> push. The user goes
 * Back or Forward -> close every layer above where history landed.
 */

interface Layer {
  id: string;
  onBack: () => void;
  /** URL for the layer's own entry (default: the URL it opens on). */
  path?: string;
  /** Has its history entry. Placed layers are always a prefix of the stack. */
  placed?: boolean;
  /** Took over a page entry: nothing of ours lies below it. */
  inPlace?: boolean;
}

interface EntryTag {
  /** A layer's entry, as opposed to a page. */
  modal?: boolean;
  /** How many layers are open on this entry. */
  depth?: number;
  /** The page load that wrote the tag; entries of an earlier load are foreign. */
  sid?: string;
  inPlace?: boolean;
}

const SESSION = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
const PAGE: EntryTag = { modal: false, inPlace: false };

// Overlays bind to Back only where there is a back button or gesture.
const TOUCH_DEVICES = '(max-width: 980px), (pointer: coarse)';
const isTouchDevice = () =>
  typeof window.matchMedia === 'function' &&
  window.matchMedia(TOUCH_DEVICES).matches;

const readTag = (): EntryTag => (window.history.state?.usr ?? {}) as EntryTag;

const writeTag = (tag: EntryTag) => {
  const state = window.history.state ?? {};
  window.history.replaceState(
    { ...state, usr: { ...(state.usr ?? {}), ...tag, sid: SESSION } },
    '',
  );
};

const currentUrl = () =>
  window.location.pathname + window.location.search + window.location.hash;

// React Router's position counter; null outside BrowserRouter.
const historyIndex = (): number | null => {
  const idx = window.history.state?.idx;
  return typeof idx === 'number' ? idx : null;
};

interface Router {
  push: (path: string, state: EntryTag) => void;
  back: () => void;
}

function createBackStack(router: Router) {
  let stack: Layer[] = [];
  let ownStepInFlight = false;
  let followingUser = false;
  let watchdog: ReturnType<typeof setTimeout> | undefined;
  // To restore the tag after a router `replace` rewrote the entry's state.
  let lastSeen: { idx: number | null; pathname: string; tag: EntryTag } | null =
    null;
  const bootIndex = historyIndex();

  const placedCount = () => stack.filter(layer => layer.placed).length;

  // The current entry, tagged if this page load has not seen it yet.
  // `landed`: history moved onto it, as opposed to the app just creating it.
  const see = (landed: boolean) => {
    let tag = readTag();
    if (tag.sid !== SESSION) {
      const sameEntry =
        lastSeen !== null &&
        lastSeen.idx !== null &&
        lastSeen.idx === historyIndex() &&
        lastSeen.pathname === window.location.pathname;
      if (sameEntry) {
        writeTag(lastSeen!.tag);
      } else if (tag.modal) {
        return { layer: true, foreign: true, depth: 0, inPlace: false };
      } else {
        const depth = landed
          ? Math.min(tag.depth ?? 0, placedCount())
          : placedCount();
        writeTag({ ...PAGE, depth });
      }
      tag = readTag();
    }
    lastSeen = {
      idx: historyIndex(),
      pathname: window.location.pathname,
      tag: { modal: tag.modal, depth: tag.depth, inPlace: tag.inPlace },
    };
    return {
      layer: tag.modal === true,
      foreign: false,
      depth: tag.depth ?? 0,
      inPlace: tag.inPlace === true,
    };
  };

  const stepBack = () => {
    ownStepInFlight = true;
    router.back();
    clearTimeout(watchdog);
    watchdog = setTimeout(() => {
      // The step never landed: nothing below. Treat this entry as a page.
      ownStepInFlight = false;
      writeTag({ ...PAGE, depth: placedCount() });
      settle();
    }, 1000);
  };

  // Bring history in step with the stack.
  const settle = () => {
    // The browser resolves a step back against the entry current when it was
    // requested, so nothing may be pushed until it lands; runs again then.
    if (ownStepInFlight) return;
    let entry = see(false);

    if (entry.foreign) {
      const reopened = stack.find(layer => !layer.placed);
      if (!reopened || placedCount() > 0) return stepBack();
      // Reloaded on a layer's own entry: the reopened layer takes it over.
      writeTag({ modal: true, inPlace: false, depth: 1 });
      reopened.placed = true;
      entry = see(false);
    }

    if (entry.depth > placedCount()) {
      if (entry.layer && !entry.inPlace && historyIndex() !== 0) {
        return stepBack();
      }
      writeTag({ ...PAGE, depth: placedCount() });
      entry = see(false);
    }

    // History is below entries of layers still open: they need new ones.
    stack.slice(entry.depth).forEach(layer => {
      layer.placed = false;
      layer.inPlace = false;
    });

    for (const layer of stack) {
      if (layer.placed) continue;
      const depth = placedCount() + 1;
      if (layer.path === currentUrl() && !see(false).layer) {
        // A link straight into this layer: it takes the page entry over
        // rather than stacking a second one. On the entry the page loaded on
        // there is nothing of ours below to step back to.
        layer.inPlace = historyIndex() === null || historyIndex() === bootIndex;
        writeTag({ modal: true, inPlace: layer.inPlace, depth });
      } else {
        router.push(layer.path ?? currentUrl(), {
          modal: true,
          depth,
          sid: SESSION,
        });
      }
      layer.placed = true;
      see(false);
    }
  };

  const onPopState = () => {
    if (!ownStepInFlight) followingUser = true;
    ownStepInFlight = false;
    clearTimeout(watchdog);

    const entry = see(true);
    // A layer's entry from before a reload: step over it.
    if (entry.foreign) return stepBack();

    if (followingUser) {
      followingUser = false;
      const closing = stack.slice(entry.depth).reverse();
      stack = stack.slice(0, entry.depth);
      closing.forEach(layer => layer.onBack());
    }
    settle();
  };

  return {
    onPopState,
    pageLoaded: () => see(true),
    pageOpened: () => {
      if (!ownStepInFlight) see(false);
    },
    dispose: () => clearTimeout(watchdog),
    add: (layer: Layer) => {
      stack = [...stack, layer];
      settle();
    },
    remove: (id: string) => {
      if (!stack.some(layer => layer.id === id)) return;
      stack = stack.filter(layer => layer.id !== id);
      settle();
    },
    // For a layer's on-screen back arrow: the system back button's path.
    stepBackFrom: (id: string) => {
      const top = stack[stack.length - 1];
      if (top?.id !== id || !top.placed || top.inPlace) return false;
      router.back();
      return true;
    },
  };
}

const NOT_BOUND = { release: () => {}, stepBack: () => false };

export const NavigationProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const navigationType = useNavigationType();

  const navigateRef = useRef(navigate);
  useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);

  const [backStack] = useState(() =>
    createBackStack({
      push: (path, state) => navigateRef.current(path, { state }),
      back: () => navigateRef.current(-1),
    }),
  );

  useEffect(() => {
    backStack.pageLoaded();
    window.addEventListener('popstate', backStack.onPopState);
    return () => {
      window.removeEventListener('popstate', backStack.onPopState);
      backStack.dispose();
    };
  }, [backStack]);

  useEffect(() => {
    if (navigationType !== 'POP') backStack.pageOpened();
  }, [location.key, navigationType, backStack]);

  // What the overlays in @heltarchat/ui and useBackDismiss register with.
  const layers = useMemo<BackLayerRegistry>(() => {
    let count = 0;
    return {
      register: (onBack, options) => {
        if (!options?.always && !isTouchDevice()) return NOT_BOUND;
        const id = `layer-${(count += 1)}`;
        backStack.add({ id, onBack, path: options?.path });
        return {
          release: () => backStack.remove(id),
          stepBack: () => backStack.stepBackFrom(id),
        };
      },
    };
  }, [backStack]);

  return <BackLayerProvider value={layers}>{children}</BackLayerProvider>;
};
